import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ProductFormSchema } from "schemas/generated/product";

import React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import { Button } from "@replo/design-system/components/button/Button";
import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { Input } from "@replo/design-system/components/input/Input";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import twMerge from "@replo/design-system/utils/twMerge";
import { MinimalTiptap } from "@v2/features/products/components/MinimalTipTapEditor";
import ProductMediaDropZone from "@v2/features/products/components/ProductMediaDropZone";
import { ProductTypeIcon } from "@v2/features/products/components/ProductTypeIcon";
import { PRODUCT_TYPE_OPTIONS } from "@v2/features/products/constants/product-type-options";
import { useProductFormEdit } from "@v2/features/products/hooks/useProductFormEdit";
import { useProductUpdate } from "@v2/features/products/hooks/useProductUpdate";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import { ArrowLeft } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

function ProductFormHeader() {
  const navigate = useNavigate();

  return (
    <div className="flex items-center w-full">
      <Button
        variant="tertiary"
        size="base"
        startEnhancer={<ArrowLeft />}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>
    </div>
  );
}

export function ProductEditForm() {
  const {
    watch,
    dispatchProductEdit,
    handleReactHookFormSubmit,
    errors,
    isProductValid: isValid,
    hasDataChanged: isDirty,
    creatingNewProduct,
    reset,
    product,
  } = useProductFormEdit();

  const { updateProduct } = useProductUpdate();
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const isMobile = useIsMobile();

  const handlePriceBlur = React.useCallback(
    (priceType: "price" | "compareAtPrice") => {
      const currentValue = watch(priceType);
      if (currentValue == null) {
        return;
      }
      const roundedValue = Number.parseFloat(currentValue.toFixed(2));
      if (roundedValue !== currentValue) {
        dispatchProductEdit({
          type: "changeProperty",
          payload: { key: priceType, value: roundedValue },
        });
      }
    },
    [dispatchProductEdit, watch],
  );

  const handleProductSave = React.useCallback(
    async (productData: ProductFormSchema) => {
      // NOTE (Kurt, 2025-09-19): We keep the externalCheckoutUrl in the form data
      // even if the checkout method is replo to have a better form experience; however
      // when the product is saved, we need to set the externalCheckoutUrl to null if the
      // checkout method is replo.
      const externalCheckoutUrl =
        productData.checkoutMethod === "external"
          ? productData.externalCheckoutUrl
          : null;

      const processedFormData = {
        ...productData,
        externalCheckoutUrl,
      };

      await updateProduct({
        product,
        formData: processedFormData,
        creatingNewProduct,
      });
      reset(productData);
      navigate(
        generatePath(routes.shops.products.list, {
          projectId: projectId ?? "",
        }),
      );
    },
    [updateProduct, product, creatingNewProduct, reset, navigate, projectId],
  );

  const selectedProductType = watch("productType");
  const selectedOption = PRODUCT_TYPE_OPTIONS.find(
    (option) => option.value === selectedProductType,
  );

  const productTypeItems: ComboboxItemWithValue[] = PRODUCT_TYPE_OPTIONS.map(
    (option) => ({
      value: option.value,
      label: option.label,
      size: "base" as const,
      startEnhancer: (
        <ProductTypeIcon icon={option.icon} color={option.color} />
      ),
    }),
  );

  const selectedItem =
    selectedProductType === "UNASSIGNED"
      ? null
      : productTypeItems.find((item) => item.value === selectedProductType) ??
        null;

  return (
    <div
      className={twMerge(
        "shadow-2xs border-0.5 border-border rounded-[10px] bg-white flex flex-col items-center gap-4 p-5",
        isMobile ? "w-full" : "w-[600px]",
      )}
    >
      <div className="flex flex-col w-full gap-2">
        <ProductFormHeader />
        <div className="flex items-center justify-between w-full gap-4">
          <div className="header-h3 truncate whitespace-nowrap min-w-0">
            {creatingNewProduct ? "New Product" : `${watch("name")}`}
          </div>
          <Button
            variant="primary"
            size="base"
            onClick={() => void handleReactHookFormSubmit(handleProductSave)()}
            disabled={!isValid || !isDirty}
            UNSAFE_className="flex-shrink-0"
          >
            {creatingNewProduct ? "Create Product" : "Save Product"}
          </Button>
        </div>
      </div>
      <div
        className={twMerge(
          "flex w-full",
          isMobile ? "flex-col gap-4" : "gap-6",
        )}
      >
        <LabeledControl
          label="Product Name"
          error={errors.name?.message}
          size="base"
          className="flex-1 min-w-0"
          required
        >
          <Input
            value={watch("name")}
            onChange={(e) => {
              return dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "name", value: e.target.value },
              });
            }}
            placeholder="Enter product name"
            size="base"
            invalid={Boolean(errors.name)}
            className="w-full"
          />
        </LabeledControl>

        <LabeledControl
          label="Product Type"
          size="base"
          className="flex-1 min-w-0"
        >
          <div className="w-full">
            <ComboboxRoot
              items={productTypeItems}
              value={selectedItem}
              onValueChange={(item) => {
                if (item) {
                  dispatchProductEdit({
                    type: "changeProperty",
                    payload: { key: "productType", value: item.value },
                  });
                }
              }}
              itemToStringValue={(item) => String(item.label)}
              isItemEqualToValue={(a, b) => a.value === b.value}
              filter={null}
            >
              <ComboboxTriggerButton
                id="product-type-selector"
                size="base"
                isPlaceholder={!selectedOption}
                className="w-full"
                startEnhancer={
                  selectedOption ? (
                    <div className="bg-surface flex h-5 w-5 shrink-0 items-center justify-center rounded-sm">
                      <ProductTypeIcon
                        icon={selectedOption.icon}
                        color={selectedOption.color}
                      />
                    </div>
                  ) : undefined
                }
              >
                {selectedOption ? selectedOption.label : "Select product type"}
              </ComboboxTriggerButton>
              <ComboboxContent sideOffset={8} side="bottom" align="start">
                <ComboboxList>
                  {(item: ComboboxItemWithValue) => (
                    <ComboboxItem key={item.value} {...item} value={item} />
                  )}
                </ComboboxList>
              </ComboboxContent>
            </ComboboxRoot>
          </div>
        </LabeledControl>
      </div>
      <div
        className={twMerge(
          "flex w-full",
          isMobile ? "flex-col gap-4" : "gap-6",
        )}
      >
        <LabeledControl
          label="Price"
          size="base"
          className="flex-1 min-w-0"
          error={errors.price?.message}
          required
        >
          <Input
            value={watch("price")?.toString() ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue =
                value === "" ? null : Number.parseFloat(value);
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "price", value: numericValue },
              });
            }}
            onBlur={() => handlePriceBlur("price")}
            placeholder="0.50"
            size="base"
            type="number"
            inputMode="decimal"
            min="0.5"
            step="0.01"
            startEnhancer={<span className="text-muted">$</span>}
            invalid={Boolean(errors.price)}
            className="w-full"
          />
        </LabeledControl>
        <LabeledControl
          label="Compare at Price"
          size="base"
          className="flex-1 min-w-0"
        >
          <Input
            value={watch("compareAtPrice")?.toString() ?? ""}
            onChange={(e) => {
              const value = e.target.value;
              const numericValue =
                value === "" ? null : Number.parseFloat(value);
              dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "compareAtPrice", value: numericValue },
              });
            }}
            onBlur={() => handlePriceBlur("compareAtPrice")}
            placeholder="0.00"
            size="base"
            type="number"
            inputMode="decimal"
            startEnhancer={<span className="text-muted">$</span>}
            className="w-full"
          />
        </LabeledControl>
      </div>
      <div className="w-full">
        <LabeledControl label="Description" size="base" className="w-full">
          <MinimalTiptap
            content={watch("description") ?? ""}
            onChange={(value) => {
              return dispatchProductEdit({
                type: "changeProperty",
                payload: { key: "description", value },
              });
            }}
            placeholder="Enter product description"
            className="w-full text-sm font-normal"
          />
        </LabeledControl>
      </div>
      <LabeledControl label="Media" size="base" className="w-full">
        <ProductMediaDropZone />
      </LabeledControl>
    </div>
  );
}
